<template>
  <b-modal
    ref="archiveRemoveModal"
    size="xl"
    :title="$t('archive.modal.takeOutFromLaboratory')"
    centered
    modal-class="remove-modal"
    qa="take-out-from-laboratory-modal"
  >
    <b-row>
      <b-col>
        <div class="making-zone px-2">
          <div class="my-2">
            <span class="ap-title ap-title__main">{{ `${$tc('archive.modal.elementCount', elements.length)} ${$t('archive.modal.toTakeOutFromLaboratory')}` }}</span>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="list px-2 mt-2">
          <div class="header-row">
            <b-row class="form-label">
              <b-col><label>{{ $tc('archive.modal.element', 1) }}</label></b-col>
              <b-col class="col-4 pr-1">
                <div class="d-flex">
                  <label>{{ $t('archive.modal.removeReason') }}</label>
                  <button
                    id="pop-reason"
                    v-testing="'remove-reason-global'"
                    class="btn btn__icon btn__icon--primary p-0 btn-secondary my-0 ml-2"
                    type="button"
                  >
                    <span class="far fa-chevron-down mr-sm-0 mr-md-3 mr-lg-5" />
                  </button>
                  <b-popover
                    target="pop-reason"
                    placement="top"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <div class="ap-popover__header">
                        <span>{{ $t('archive.modal.batchReason') }}</span>
                      </div>
                      <ul>
                        <li
                          v-for="(unarchivingReason, index) in unarchivingReasons"
                          :key="index"
                        >
                          <b-button @click="onGeneralUnarchivingReasonSelected(unarchivingReason)">
                            {{ unarchivingReason.name }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </div>
              </b-col>
              <b-col class="col-5">
                <div class="d-flex">
                  <label>{{ $t('archive.modal.return') }}</label>
                  <button
                    id="pop-return"
                    v-testing="'return-global'"
                    class="btn btn__icon btn__icon--primary p-0 btn-secondary my-0 ml-2"
                    type="button"
                  >
                    <span class="far fa-chevron-down mr-sm-0 mr-md-3 mr-lg-5" />
                  </button>
                  <b-popover
                    target="pop-return"
                    placement="top"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <div class="ap-popover__header">
                        <span>{{ $t('shipments.modal.batchReturn') }}</span>
                      </div>
                      <ul>
                        <li
                          v-testing="'should-be-returned-all'"
                        >
                          <b-button @click="onGeneralUnarchivingReasonReturnOptionSelected(true)">
                            {{ $tc('archive.modal.shouldBeReturned') }}
                          </b-button>
                        </li>
                        <li
                          v-testing="'should-not-be-returned-all'"
                        >
                          <b-button @click="onGeneralUnarchivingReasonReturnOptionSelected(false)">
                            {{ $t('archive.modal.shouldNotBeReturned') }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </div>
              </b-col>
            </b-row>
          </div>
          <div
            v-testing="'elements-table'"
            class="mt-2"
          >
            <div
              v-for="(element, index) in elements"
              :key="index"
              v-testing="'element-row'"
              class="element-row"
            >
              <b-row class="form-label">
                <b-col v-testing="'element-label'">
                  <div class="d-flex">
                    <div class="row-icon d-flex align-items-center justify-content-center ap-title ap-title__section">
                      <span :class="elementIcon" />
                    </div>
                    <span class="ap-title ap-title__section">{{ element.label }}</span>
                  </div>
                </b-col>
                <b-col
                  v-testing="'reason'"
                  class="col-4 pr-1"
                >
                  <span class="row-info">{{ element.unarchivingReason && element.unarchivingReason.name }}</span>
                  <button
                    :id="'pop-reason-element-' + index"
                    v-testing="'remove-reason-specific'"
                    class="btn btn__icon btn__icon--primary p-0 btn-secondary my-0 ml-2"
                    type="button"
                  >
                    <span class="far fa-chevron-down mr-sm-0 mr-md-3 mr-lg-5" />
                  </button>
                  <b-popover
                    :target="'pop-reason-element-' + index"
                    placement="top"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <div class="ap-popover__header">
                        <span>{{ $t('archive.modal.removeReason') }}</span>
                      </div>
                      <ul>
                        <li
                          v-for="(unarchivingReason) in unarchivingReasons"
                          :key="unarchivingReason.name"
                          v-testing="`reason-id-${unarchivingReason.id}`"
                        >
                          <b-button @click="onUnarchivingReasonSelected(element, index, unarchivingReason)">
                            {{ unarchivingReason.name }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </b-col>
                <b-col
                  v-testing="'return-option'"
                  class="col-5"
                >
                  <span class="row-info">{{ getUnarchivingWithReturnOptionTranslation(element.unarchivingWithReturnOption) }}</span>
                  <button
                    v-testing="'delete-element'"
                    v-tooltip="$t('archive.modal.deleteElement')"
                    class="btn btn__icon btn__icon--primary p-0 btn-secondary my-0 mr-1"
                    type="button"
                    @click="elements.splice(index, 1)"
                  >
                    <span class="far fa-times mr-2" />
                  </button>
                  <button
                    :id="'pop-return-element-' + index"
                    v-testing="'return-specific'"
                    class="btn btn__icon btn__icon--primary p-0 btn-secondary my-0"
                    type="button"
                  >
                    <span class="far fa-chevron-down mr-4" />
                  </button>
                  <b-popover
                    :target="'pop-return-element-' + index"
                    placement="top"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <div class="ap-popover__header">
                        <span>{{ $t('archive.modal.return') }}</span>
                      </div>
                      <ul>
                        <li
                          v-testing="'should-be-returned'"
                        >
                          <b-button @click="onUnarchivingReasonReturnOptionSelected(element, index, true)">
                            {{ $tc('archive.modal.shouldBeReturned') }}
                          </b-button>
                        </li>
                        <li
                          v-testing="'should-not-be-returned'"
                        >
                          <b-button @click="onUnarchivingReasonReturnOptionSelected(element, index, false)">
                            {{ $t('archive.modal.shouldNotBeReturned') }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <div
      slot="modal-footer"
      class="footer-banner px-2 w-100"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            v-testing="'cancel'"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <vt-loading-button
            qa="archive-remove-button"
            class="btn--primary btn-sm"
            :params="{ promise: takeElementsOutFromLaboratory }"
          >
            <span class="far fa-check mr-2" />
            {{ $t('global.apply') }}
          </vt-loading-button>
        </div>
      </div>
    </div>
    <!-- TODO: Mocked data, pending development  -->
    <modal-confirmation-checkable
      id="takeOutFromLaboratoryConfirmation"
      ref="confirmationModal"
      :content="$t('archive.modal.takeOutFromLaboratoryMessage', { coreEntity: 'La muestra', entityLabel: 'H3B20-000006-A', reason: 'Retallado' })"
      :checkable="$t('archive.modal.takeOutFromLaboratoryCheckboxMessage')"
    />
  </b-modal>
</template>

<script>
import ModalConfirmationCheckable from '@/components/modals/ModalConfirmationCheckable'

import { COREENTITY_VALUES } from '@/api/resources/incident/incidentServices'
import { PORTION_TYPES } from '@/api/resources/portionTypeService'
import { SUBPORTION_TYPES } from '@/api/resources/subportionTypes'
import archiveService from '@/modules/archiveModule/services/archiveService'
import takeOutFromLaboratoryService from '@/modules/archiveModule/services/takeOutFromLaboratoryService'

export default {
    name: 'UnarchivingModal',
    components: {
        ModalConfirmationCheckable
    },
    props: {
        entityType: {
            type: Object,
            required: true,
            validator: value => [
                COREENTITY_VALUES.SAMPLE.code,
                COREENTITY_VALUES.PORTION.code,
                COREENTITY_VALUES.SUBPORTION.code
            ].includes(value.code)
        },
        entitySubtype: {
            type: String,
            required: false,
            validator: value => [
                PORTION_TYPES.PARAFFIN_BLOCK.key,
                PORTION_TYPES.FROZEN_BLOCK.key,
                SUBPORTION_TYPES.SLIDE.key
            ].includes(value)
        }
    },
    data() {
        return {
            COREENTITY_VALUES: COREENTITY_VALUES,
            elements: [],
            unarchivingReasons: []
        }
    },
    computed: {
        laboratoryId() {
            return this.$store.getters.labId
        },
        elementIcon() {
            if (this.entityType === COREENTITY_VALUES.SAMPLE) {
                return 'far fa-prescription-bottle'
            } else if (this.entitySubtype === PORTION_TYPES.PARAFFIN_BLOCK.key) {
                return 'icon-block'
            } else if (this.entitySubtype === PORTION_TYPES.FROZEN_BLOCK.key) {
                return 'icon-frozen-block'
            } else if (this.entitySubtype === SUBPORTION_TYPES.SLIDE.key) {
                return 'icon-porta'
            }
            return ''
        }
    },
    async created() {
        const [error, response] = await this.toCall(takeOutFromLaboratoryService.getUnarchivingReasons(this.laboratoryId))
        if (error) {
            this.showErrorModalGlobalFromErrorResponse(error)
        } else {
            this.unarchivingReasons = response
        }
    },
    methods: {
        show(elements) {
            this.elements = this.mapEntityToModalElements(elements)
            this.$refs.archiveRemoveModal.show()
        },
        mapEntityToModalElements(elements) {
            if (!elements) {
                return []
            }
            return elements.map(entity => {
                return {
                    ...entity,
                    unarchivingReason: this.unarchivingReasons[0],
                    unarchivingWithReturnOption: false
                }
            })
        },
        mapEntityToUnarchiveRequest(elements) {
            if (!elements) {
                return []
            }
            return elements.map(element => {
                const archiveRequest = archiveService.getEntityHierarchicalIds(element)
                return {
                    idHierarchy: archiveRequest,
                    id: element.id,
                    label: element.label,
                    unarchivingReasonId: element.unarchivingReason.id,
                    unarchivingWithReturnOption: element.unarchivingWithReturnOption
                }
            })
        },
        hide() {
            this.$refs.archiveRemoveModal.hide()
        },
        showUnarchivingConfirmation() {
            this.$refs.confirmationModal.show()
        },
        getUnarchivingWithReturnOptionTranslation(value) {
            if (value) {
                return this.$t('archive.modal.shouldBeReturned')
            }
            return this.$t('archive.modal.shouldNotBeReturned')
        },
        onUnarchivingReasonSelected(element, index, unarchivingReason) {
            element.unarchivingReason = unarchivingReason
            this.$root.$emit('bv::hide::popover', 'pop-reason-element-' + index)
        },
        onUnarchivingReasonReturnOptionSelected(element, index, shouldBeReturned) {
            element.unarchivingWithReturnOption = shouldBeReturned
            this.$root.$emit('bv::hide::popover', 'pop-return-element-' + index)
        },
        onGeneralUnarchivingReasonSelected(unarchivingReason) {
            this.elements.forEach(element => {
                element.unarchivingReason = unarchivingReason
            })
            this.$root.$emit('bv::hide::popover', 'pop-reason')
        },
        onGeneralUnarchivingReasonReturnOptionSelected(shouldBeReturned) {
            this.elements.forEach(element => {
                element.unarchivingWithReturnOption = shouldBeReturned
            })
            this.$root.$emit('bv::hide::popover', 'pop-return')
        },
        async takeElementsOutFromLaboratory() {
            const elementsWithError = []
            const [error, response] = await this.toCall(takeOutFromLaboratoryService.takeOutFromLaboratory(this.laboratoryId, this.mapEntityToUnarchiveRequest(this.elements), this.entityType))
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            } else if (response && response.length) {
                let errorMessage = ''
                response.forEach(failedElementResponse => {
                    const elementIndex = this.elements.findIndex(element => element.id === failedElementResponse.id)
                    const elementWithError = this.elements[elementIndex]
                    if (elementWithError) {
                        errorMessage = errorMessage + `<p>${elementWithError.label}: ${failedElementResponse.errorMessage}</p>`
                        elementsWithError.push(elementWithError)
                    } else {
                        errorMessage = errorMessage + `<p>${failedElementResponse.errorMessage}</p>`
                    }
                })
                this.elements = elementsWithError
                this.showErrorModalGlobal(this.$t('archive.error.errorUnarchiving') + `: ${errorMessage}`)
            } else {
                this.showToastSuccessGlobal(this.$t('archive.toastMessages.selectionUnarchived'))
                this.hide()
            }
            this.$emit('elements-taken-out-from-laboratory')
        }
    }
}
</script>

<style lang="scss">
    .remove-modal {
        .making-zone {
            .title {
                color: vt-color(primary);
                font-size: font-size(very-normal);
            }
        }
        .nav-link {
            padding: 6px;
            div {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        }
        .arrow {
            display: none;
        }
        .list {
            height: 440px;
            overflow-y: auto;
            overflow-x: hidden;
            &.large-list {
                height: 300px;
            }
            .row-info{
                color: vt-color(dark-text);
            }
            .row-icon {
                width: 20px;
            }
        }
        .header-row {
            position: sticky;
            top: 0;
            background: #FFF;
            z-index: 1;
        }
        .element-row {
            border-bottom: 1px solid vt-color(grid-general-border);
            padding: 7px 0;
            &:hover {
                background: vt-color(light-primary);
            }
        }
        .ctx-menu {
            padding-top: 0;
        }
        .shipping-box {
            margin: 10px 6px;
            border: 1px solid vt-color(base-text, .5);
            border-radius: 4px;
            box-shadow: 0px 1px 1px 0px rgba(141, 141, 143, 0.3);
            border-left: 3px solid vt-color(primary);
            .title {
                color: vt-color(primary);
            }
        }
    }

    .remove-pop {
        padding: 0 !important;
        ul {
            padding: 0;
            margin: 5px 0;
            li {
                list-style: none;
                padding: 2px 20px;
                font-size: 12px;
                cursor: pointer;
                &:hover {
                    background: vt-color(light-primary);
                }
            }
        }
        .pop-header {
            background: vt-color(base-text, .9);
            padding: 8px 12px;
            font-weight: 700;
        }
    }
</style>
